import type { Configuration, ReceiverCustomData } from '@setplex/pureya'
import type { DRM } from '@setplex/tria-api'
import type { UsePlayer } from '~/widgets/Player/hooks/usePlayer'

import { useUnit } from 'effector-react'
import { useMemo, useRef, useState } from 'react'
import { chromecastFeatureModel } from '~/features/chromecast'
import { logger } from '~/shared/logger'

/* Only widvine drm system supported by chromecast */
// https://developers.google.com/cast/docs/media

/**
 * Chromecast configuration hook
 */
export function useChromecast(params: {
  title?: string
  subtitle?: string
  drm?: DRM
  player?: UsePlayer
  contentId?: string
}): {
  chromecast: Configuration['chromecast']
  isConnected: boolean
  stoppedTime: number | undefined
} {
  const [isConnected, setIsConnected] = useState(false)
  const [stoppedTime, setStoppedTime] = useState<number | undefined>()

  const enabled = useUnit(chromecastFeatureModel.$enabled)

  const latestParams = useRef(params)
  latestParams.current = params

  // clear saved stopped time right after it probably was aplied once in useLastPositionSave
  params.player?.events.on?.('loadeddata', () => {
    setStoppedTime(undefined)
  })

  const chromecast = useMemo(
    () => ({
      enabled,
      requestTitleFn: () => latestParams.current.title,
      requestSubtitleFn: () => latestParams.current.subtitle,
      // this needed for receiver see
      // https://github.com/bldsoft/stb-chromecast/tree/main

      requestCustomDataFn: (): ReceiverCustomData => {
        const drm = latestParams.current.drm
        let exoPlayerConfig = {}

        if (drm) {
          const widewineSystem = drm.la.WIDEVINE

          if (!widewineSystem) return { exoPlayerConfig }

          exoPlayerConfig = {
            headers: widewineSystem.headers,
            licenseUrl: widewineSystem.licenseUrl,
            // https://developers.google.com/cast/docs/reference/web_receiver/cast.framework#.ContentProtection
            // uppercase is necessary!
            protectionSystem: 'WIDEVINE',
          }
        }

        return { exoPlayerConfig }
      },

      // onTrackSetSuccess: noop,
      onTrackSetError: () => logger.error('Chromecast: Failed to set track.'),
      onMediaInfoLoadError: (reason: string) =>
        logger.error(
          `Chromecast: Failed to load media on receiver due to: `,
          reason
        ),
      onConnected: () => setIsConnected(true),
      onDisconnected: (time: number) => {
        setIsConnected(false)
        setStoppedTime(time)
      },
    }),
    // callbacks links should not change
    // oxlint-disable-next-line react/exhaustive-deps
    [enabled]
  )

  return {
    chromecast,
    isConnected,
    stoppedTime,
  }
}
